import { useCallback, useMemo } from "react";
import { get } from "lodash";
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import UserService from "services/User";
import useAuthorized from "hooks/useAuthorized";
import { addResizedColumns } from "./useTableConfig";
import {
  CAN_CALL_PERMISSIONS,
  CAN_EDIT_STATUS_PERMISSIONS,
  CAN_SET_CAMPAIGN_TARGET_PERMISSIONS,
  getCampaignContactsTableColumns,
  getCampaignContactsTableFilters,
} from "routes/campaign/components/campaign_contact_table/CampaignContactsTableColumnsAndFilters";
import { handleFetchTags } from "./useContactsTable";
import { CAMPAIGN_CONTACTS_TABLE_CONSTANTS } from "routes/campaign/components/campaign_contact_table/CampaignContactsTableV3";
import { formatUsersForFilter } from "helpers/user";
import { campaignContactKeys } from "routes/campaign/components/campaign_contact_table/queryKeys";

const handleFetchUsers = async () => {
  const rawUsers = await UserService.getUsers();
  return formatUsersForFilter(rawUsers);
};

export const useCampaignContactTable = ({
  className = CAMPAIGN_CONTACTS_TABLE_CONSTANTS.className,
  entityId = null,
  campaignId = null,
  includedInCampaign = {},
  handleAddOrRemoveCampaignContact,
  tableName = CAMPAIGN_CONTACTS_TABLE_CONSTANTS.tableName,
  activeSession = null,
}) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", "options", className],
    queryFn: () => handleFetchTags(className),
  });

  const { data: users, isSuccess: isUsersFetched } = useQuery({
    queryKey: ["users"],
    queryFn: handleFetchUsers,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const canSetCampaignTarget = useAuthorized(
    CAN_SET_CAMPAIGN_TARGET_PERMISSIONS
  );
  const canCall = useAuthorized(CAN_CALL_PERMISSIONS);
  const canEditStatus = useAuthorized(CAN_EDIT_STATUS_PERMISSIONS);

  const queryKey = useMemo(
    () => campaignContactKeys.list({ campaignId, entityId }),
    [campaignId, entityId]
  );

  const invalidateData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      // Including or excluding a contact changes the detail sidebar's contact
      // counts, and the sidebar stays mounted behind the tab.
      queryClient.invalidateQueries({ queryKey: ["campaign"] }),
    ]);
  }, [queryKey]);

  const filters = useMemo(() => {
    if (!isTagsFetched || !isUsersFetched) {
      return [];
    }
    const finalFilters = getCampaignContactsTableFilters({
      campaignId,
      tags,
      users,
    });

    return finalFilters;
  }, [tags, users, isTagsFetched, isUsersFetched, campaignId]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getCampaignContactsTableColumns({
      entityId,
      campaignId,
      includedInCampaign,
      handleAddOrRemoveCampaignContact,
      activeSession,
      invalidateData,
      canSetCampaignTarget,
      canCall,
      canEditStatus,
    });
    const campaignContactsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: campaignContactsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    tableSettings,
    isTableSettingsFetched,
    handleAddOrRemoveCampaignContact,
    entityId,
    campaignId,
    includedInCampaign,
    activeSession,
    invalidateData,
    tableName,
    canSetCampaignTarget,
    canCall,
    canEditStatus,
  ]);
  return { filters, columns, tags, users, queryKey, invalidateData };
};
